// Ad attribution and conversion events for the landing page.
//
// The first visit that carries utm_* params is remembered (first touch), so a
// prospect who clicks an ad today and comes back directly next week is still
// credited to the ad when they submit the form.

export interface Attribution {
  utm: { source?: string; medium?: string; campaign?: string; content?: string; term?: string };
  referrer?: string;
}

const STORAGE_KEY = 'ls_attribution';
const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'] as const;

export function captureAttribution(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const utm: Attribution['utm'] = {};
  for (const key of UTM_KEYS) {
    const value = params.get(`utm_${key}`);
    if (value) utm[key] = value;
  }
  // Ignore same-site referrers (e.g. coming back from the demo studio)
  const referrer = document.referrer && !document.referrer.includes(window.location.hostname)
    ? document.referrer
    : undefined;
  const current: Attribution = { utm, referrer };

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && Object.keys(utm).length === 0) return JSON.parse(stored) as Attribution;
    if (Object.keys(utm).length > 0 || referrer) localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Storage blocked (private mode) — fall back to this visit only
  }
  return current;
}

type Tracker = (...args: unknown[]) => void;

/**
 * Fire a conversion on whichever ad tags are installed (Meta Pixel, Google tag).
 * No-ops until those snippets are added to public/index.html.
 */
export function trackEvent(event: 'Lead' | 'DemoClick'): void {
  const w = window as unknown as { fbq?: Tracker; gtag?: Tracker };
  if (event === 'Lead') {
    w.fbq?.('track', 'Lead');
    w.gtag?.('event', 'generate_lead');
  } else {
    w.fbq?.('trackCustom', 'DemoClick');
    w.gtag?.('event', 'demo_click');
  }
}
